import { useQueryClient } from "@tanstack/react-query";
import { Map as MapIcon } from "lucide-react";
import { useCallback } from "react";

import type { Exercise } from "../api/types";
import { attentionSelector, designate, designateSoon } from "../components/attention";
import { Button } from "../components/ui";
import { k, t } from "../i18n/i18n";
import { useUi } from "../state/ui";
import { DRONES_FIELD, type MissionDataField, missionDataTarget, OPEN_DRONES_SETTINGS } from "./missionDataTarget";

export type { MissionDataField } from "./missionDataTarget";

/** Avis court qui accompagne la désignation de chaque champ. */
const FIELD_NOTICE: Record<MissionDataField, string> = {
  "dossier-level": k("Choisissez le niveau de l'ordre."),
  "dossier-camp": k("Choisissez un camp et une mission pour déployer les unités."),
  "dossier-place": k("Nommez le lieu du nouveau terrain : ville, coordonnées ou MGRS."),
  "dossier-odb": k("Choisissez l'ordre de bataille de la force."),
  "dossier-mission": k("Choisissez la mission de la force."),
  "dossier-name": k("Nommez le dossier pour l'enregistrer."),
  "dossier-centac-rotation": k("Saisissez le numéro de la rotation, par exemple 08/26."),
  "dossier-weather": k("Réglez la météo de l'exercice."),
  "dossier-enemy": k("Choisissez l'ennemi."),
  "dossier-drones": k("Réglez l'emploi des drones."),
};

/** Délai d'attente de la rubrique « Drones » (vue des Données initiales qui se monte et entre en glissé) puis de
 *  l'ouverture de ses réglages repliés. */
const DRONES_MOUNT_MS = 1600;
const DRONES_OPEN_MS = 900;

/** Réglage visible : monté et hors d'un dépliage fermé (`Disclosure`, `data-open="false"`). */
function shown(selector: string): HTMLElement | null {
  const element = document.querySelector<HTMLElement>(selector);
  if (!element) return null;
  return typeof element.closest === "function" && element.closest('[data-open="false"]') ? null : element;
}

/** Cherche toutes les 50 ms, pendant `delay` ms au plus, ce que `find` rend, puis appelle `done` (null : rien). */
function whenFound(find: () => HTMLElement | null, delay: number, done: (element: HTMLElement | null) => void) {
  const started = Date.now();
  const tick = () => {
    const element = find();
    if (element || Date.now() - started >= delay) done(element);
    else window.setTimeout(tick, 50);
  };
  window.setTimeout(tick, 50);
}

/** Désigne la rubrique « Drones » des Données initiales, la vue déjà demandée (amendement A2) : une fois la
 *  rubrique montée (la vue se monte et entre en glissé), l'événement `OPEN_DRONES_SETTINGS` lui demande d'ouvrir
 *  ses réglages repliés et de désigner `target` ; si la rubrique ne l'a pas désigné une fois ouvert, il l'est ici
 *  (jamais deux fois). Sans `target`, la rubrique entière est désignée. L'événement n'est jamais émis avant le
 *  montage de la rubrique. */
export function designateDrones(target?: string) {
  const section = attentionSelector(DRONES_FIELD);
  whenFound(
    () => document.querySelector<HTMLElement>(section),
    DRONES_MOUNT_MS,
    (element) => {
      window.dispatchEvent(new CustomEvent(OPEN_DRONES_SETTINGS, { detail: target ? { target } : {} }));
      if (!target) {
        designate(element, "ring");
        return;
      }
      whenFound(
        () => shown(attentionSelector(target)),
        DRONES_OPEN_MS,
        (setting) => {
          if (setting?.classList.contains("wayfinding")) return; // déjà désigné par la rubrique
          designate(setting ?? element, setting ? "auto" : "ring");
        },
      );
    },
  );
}

/** Cible effective d'un champ : la liste déroulante qu'il porte (mise au point et cernée de bleu), sinon le champ
 *  ou le groupe lui-même (niveau de l'ordre : cartes de choix). `Select` ne transmet pas `data-attention` :
 *  l'attribut est posé sur son conteneur dans les Données initiales. Lieu de l'exercice : la liste attendue, sous
 *  `[data-designate]` (camp ou terrain, jamais la première carte de choix) ; sans liste (« Terrain libre » sans
 *  terrain de la licence), le bloc entier — le bouton « Placer le terrain » pulse déjà. */
function designateField(field: MissionDataField) {
  const own = attentionSelector(field);
  if (field === "dossier-camp") {
    designateSoon(`${own} [data-designate] [role="combobox"]`, "field", 1200, (element) => {
      if (!element) designate(document.querySelector<HTMLElement>(own), "ring");
    });
    return;
  }
  if (field === "dossier-weather") {
    designateSoon(own, "ring"); // réglage entier (tuiles de choix, aperçu)
    return;
  }
  if (field === "dossier-drones") {
    // Réglages repliés (amendement A2) : l'ouverture est demandée à la section une fois montée, puis elle est désignée.
    designateDrones();
    return;
  }
  const inner = `${own} [role="combobox"]`;
  const hasInner = field === "dossier-odb" || field === "dossier-mission" || field === "dossier-place" || field === "dossier-enemy";
  designateSoon(hasInner ? inner : own, hasInner ? "field" : "auto");
}

/** Ouvre les Données initiales et désigne ce qui manque aux panneaux de la carte (R12, R13), selon
 *  `missionDataTarget` : le champ manquant (niveau, camp, ordre de bataille, ou le champ propre au panneau,
 *  `fallback`), ou la demande d'enregistrement (`requestSave`), qui désigne le nom du dossier s'il est vide, sinon
 *  « Créer le dossier » ou « Enregistrer », et ramène ensuite à la vue d'origine. `exercise` : le dossier
 *  enregistré ouvert, s'il est connu. */
export function openMissionData(options: { exercise?: Pick<Exercise, "map_slug" | "parameters"> | null; fallback?: MissionDataField } = {}) {
  const ui = useUi.getState();
  const params = ui.deploymentParams;
  const target = missionDataTarget({
    exercise: options.exercise,
    deployed: Boolean(params),
    saved: Boolean(params?.exercise_id),
    fallback: options.fallback,
  });
  if (target.kind === "save") {
    ui.requestSave();
    return;
  }
  ui.setView("mission");
  if (target.kind === "view") return;
  designateField(target.field);
  ui.notify({ text: t(FIELD_NOTICE[target.field]), tone: "info" });
}

/** Ouvre les Données initiales et désigne ce champ, avec son avis court (préalable manquant d'une autre vue : niveau
 *  ou mission de l'ordre avant le tracé d'une zone libre, nom du dossier avant « Zone libre… ») ; `notice` : un avis
 *  propre à la demande (déjà traduit) à la place de l'avis du champ. */
export function showMissionField(field: MissionDataField, notice?: string) {
  const ui = useUi.getState();
  ui.setView("mission");
  designateField(field);
  ui.notify({ text: notice ?? t(FIELD_NOTICE[field]), tone: "info" });
}

/** `openMissionData` avec le dossier ouvert lu dans le cache (liste des dossiers) au moment de l'appel. */
export function useOpenMissionData(fallback?: MissionDataField) {
  const queryClient = useQueryClient();
  return useCallback(() => {
    const exerciseId = useUi.getState().exerciseId;
    const exercise = exerciseId ? queryClient.getQueryData<Exercise[]>(["exercises"])?.find((e) => e.id === exerciseId) : null;
    openMissionData({ exercise, fallback });
  }, [queryClient, fallback]);
}

/** Lien unique des états vides des panneaux : « Ouvrir les Données initiales » (`Button variant="link"`).
 *  `fallback` : champ propre au panneau quand le dossier enregistré ne manque de rien (la mission, par exemple). */
export function OpenMissionDataButton({ fallback }: { fallback?: MissionDataField } = {}) {
  const open = useOpenMissionData(fallback);
  return (
    <Button variant="link" onClick={open}>
      <MapIcon size={14} /> {t("Ouvrir les Données initiales")}
    </Button>
  );
}
